<script setup lang="ts">
import { computed } from 'vue'

import { useCreativeBenchmark } from '../../composables/useCreativeBenchmark'
import type { BenchmarkDimension } from '../../models/benchmark.types'

import {
  DataVisible,
  ReportMediaGalleryCell,
  useReportCellFormatter,
  useReportSchedules,
  type ReportColumn,
  type ReportFilter,
  type ScheduleTab,
  type ScheduleTabValue,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui'

const props = defineProps<{
  dimensions: BenchmarkDimension[]
  defaultPostData: string
}>()

const REPORT_ID = 66
const PARTITION = 'tiktok_ads_ad'
const REQUEST_FILTERS: ReportFilter[] = [
  { field: 'marketing_channel', match_mode: 'equals', value: 'tiktok_ads' },
  { field: 'p_cpm', match_mode: 'greater_than', value: 0 },
]
const CARD_DIMENSIONS = ['campaign_name', 'ad_name', 'objective']
const CARD_METRICS = ['conversions_value', 'roas']
const METRIC_ORDER = [
  'cost',
  'impressions',
  'clicks',
  'cost_per_conversion',
  'cpc',
  'p_cpc',
  'cpm',
  'p_cpm',
  'p_cpa',
  'ctr',
  'p_ctr',
  'conversions',
  'p_cvr',
  'p_video_views_p50',
  'p_average_video_play',
]

const { t } = useI18n()
const format = useFormat()
const { formatCell } = useReportCellFormatter()
const schedules = useReportSchedules()
const benchmark = useCreativeBenchmark({
  dimensions: () => props.dimensions,
  defaultPostData: () => props.defaultPostData,
})

const presentationDimensions = ['preview_ss_url', 'image_url', 'thumbnail_url']
const scheduleTab = computed<ScheduleTab | null>(() => {
  const selection = schedules.checkScheduleTabs(REPORT_ID, PARTITION)
  const value: ScheduleTabValue | undefined = Array.isArray(selection) ? selection[0] : selection
  return typeof value === 'object' && value !== null ? value : null
})
const partitionFields = computed(() => {
  const enumerated = scheduleTab.value?.columns ?? []
  if (enumerated.length > 0) return new Set(enumerated)
  return new Set([...(scheduleTab.value?.dimensions ?? []), ...(scheduleTab.value?.metrics ?? [])])
})
const presentationMetrics = computed(() =>
  partitionFields.value.size === 0
    ? METRIC_ORDER
    : METRIC_ORDER.filter((field) => partitionFields.value.has(field)),
)
const cardDimensions = computed(() =>
  partitionFields.value.size === 0
    ? CARD_DIMENSIONS
    : CARD_DIMENSIONS.filter((field) => partitionFields.value.has(field)),
)
const cardMetrics = computed(() => [
  ...presentationMetrics.value,
  ...(partitionFields.value.size === 0
    ? CARD_METRICS
    : CARD_METRICS.filter((field) => partitionFields.value.has(field))),
])
const availableFields = computed(() => [
  ...new Set([
    ...presentationDimensions,
    ...presentationMetrics.value,
    ...cardDimensions.value,
    ...cardMetrics.value,
    ...partitionFields.value,
  ]),
])

function isPercentile(column: ReportColumn, value: unknown): boolean {
  return column.field.startsWith('p_') && Number.isFinite(Number(value))
}

function percentileLabel(value: unknown): string {
  return t('commerceAnalytics.benchmark.score', {
    value: format.formatNumber(Number(value), { maxFrac: 0 }),
  })
}

function percentileClasses(band: string): string {
  switch (band) {
    case 'poor':
      return 'text-danger bg-danger-soft'
    case 'belowAvg':
      return 'text-accent bg-accent-soft'
    case 'average':
      return 'text-warning bg-warning-soft'
    case 'good':
      return 'text-success bg-success-soft'
    case 'excellent':
      return 'text-primary-fg bg-success'
    default:
      return ''
  }
}
</script>

<template>
  <div
    class="benchmark-panel grid min-w-0 gap-md [&_th]:text-start [&_th>button:first-child]:justify-start"
  >
    <Alert v-if="benchmark.notice.value" tone="warning" data-testid="benchmark-notice">
      {{ t('commerceAnalytics.benchmark.customUnavailable') }}
    </Alert>

    <DataVisible
      id="66-TiktokBenchmark"
      :report-id="REPORT_ID"
      :default-post-data="benchmark.reportPostData.value"
      :dimensions="presentationDimensions"
      :request-dimensions="benchmark.requestDimensions.value"
      :metrics="presentationMetrics"
      :card-dimensions="cardDimensions"
      :card-metrics="cardMetrics"
      :available-fields="availableFields"
      :partition-name="scheduleTab?.partition_name ?? PARTITION"
      channel="tiktok_ads"
      :table-modes="false"
      :show-total="false"
      :filters="REQUEST_FILTERS"
      sort-field="cost"
      :sort-order="-1"
      view="table"
      :allowed-views="['table', 'card']"
      :edges="['image_id', 'video_id']"
      :data-loader="benchmark.dataLoader"
      :add-compare="true"
      v-bind="{
        ariaLabel: t('commerceAnalytics.benchmark.tableLabel'),
        emptyLabel: t('commerceAnalytics.benchmark.empty'),
        errorLabel: t('commerceAnalytics.benchmark.error'),
        retryLabel: t('commerceAnalytics.benchmark.retry'),
      }"
    >
      <template #cell="{ row, value, column }">
        <ReportMediaGalleryCell
          v-if="column.isImage"
          :row="row"
          :alt="t('commerceAnalytics.benchmark.mediaAlt')"
        />
        <span
          v-else-if="isPercentile(column, value)"
          class="benchmark-panel__percentile inline-flex py-2xs px-xs rounded-sm text-[0.6875rem] font-semibold whitespace-nowrap"
          :class="percentileClasses(benchmark.bandOf(Number(value)))"
          :data-benchmark-band="benchmark.bandOf(Number(value))"
        >
          {{ percentileLabel(value) }}
        </span>
        <template v-else>{{ formatCell(value, column) }}</template>
      </template>
    </DataVisible>
  </div>
</template>
